import React, { useState, useEffect } from 'react';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import api from '../utils/api';
import SearchableSelect from '@/components/ui/SearchableSelect';
import DebtorsAgingTab from '../components/sales_reports/DebtorsAgingTab';
import * as XLSX from 'xlsx';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import DateRangeFilter from '../components/sales-reports/DateRangeFilter';
import AnalysisChart from '../components/sales-reports/AnalysisChart';
import CustomerProductFilters from '../components/sales-reports/CustomerProductFilters';
import DocumentPrintView from '../components/procurement/DocumentPrintView';
import { formatDateDMY } from '../utils/dateFormat';
import DateInputDMY from '../components/DateInputDMY';
import { hasPermission } from '../utils/permissions';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export default function SalesReports() {
  const { user } = useAuth();
  const { formatAmount, companySettings } = useCurrency();
  const { selectedFy } = useFiscalYear();
  const [activeTab, setActiveTab] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });

  // Report data states
  const [monthlyData, setMonthlyData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [salesRepData, setSalesRepData] = useState([]);
  const [analysisData, setAnalysisData] = useState({ by_category: [], by_brand: [], by_division: [], by_model: [], by_subcategory: [] });
  const [customerProductData, setCustomerProductData] = useState([]);  // NEW: Customer-Product Purchase Report
  const [invoices, setInvoices] = useState([]);  // NEW: Store all invoices for filtering
  const [products, setProducts] = useState([]);  // NEW: Store products for dropdowns
  const [customerProductFilters, setCustomerProductFilters] = useState({
    start_date: '',
    end_date: '',
    customer_id: '',
    product_name: '',
    part_number: ''
  });
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [partNumberSearchTerm, setPartNumberSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showPartNumberDropdown, setShowPartNumberDropdown] = useState(false);

  // Search states for each tab
  const [searchCustomer, setSearchCustomer] = useState('');
  const [searchProduct, setSearchProduct] = useState('');
  const [searchSalesRep, setSearchSalesRep] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [searchBrand, setSearchBrand] = useState('');
  const [searchDivision, setSearchDivision] = useState('');
  const [searchModel, setSearchModel] = useState('');
  const [searchSubCategory, setSearchSubCategory] = useState('');
  const [searchCustomerProduct, setSearchCustomerProduct] = useState('');  // NEW: Search for customer-product report

  // Customer Ledger tab state
  const [customers, setCustomers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [ledgerFilters, setLedgerFilters] = useState({
    customer_id: '',
    start_date: '',
    end_date: '',
    payment_status: '',
    sales_rep_id: '',
    pending_only: false,
  });
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [printingLedger, setPrintingLedger] = useState(null);
  const [emailingLedger, setEmailingLedger] = useState(false);

  // Permission-driven access: any user with sales_reports.view passes
  // (per-user permission matrix takes precedence over role defaults).
  useEffect(() => {
    if (user && !hasPermission(user, 'sales_reports', 'view')) {
      window.location.href = '/dashboard';
    }
    
    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.product-search-dropdown')) {
        setShowProductDropdown(false);
      }
      if (!event.target.closest('.part-number-search-dropdown')) {
        setShowPartNumberDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [user]);

  useEffect(() => {
    if (user && hasPermission(user, 'sales_reports', 'view')) {
      fetchReports();
    }
  }, [user]);

  // Sync global Fiscal Year selection → the report's date range. Whenever the
  // user switches FY in the header, reseed dateRange and immediately refetch
  // reports using the *new* FY range (passed explicitly so we don't depend
  // on stale `dateRange` closure). Users can still override via the date
  // pickers below.
  useEffect(() => {
    if (!selectedFy) return;
    const nextRange = {
      start_date: selectedFy.start_date,
      end_date: selectedFy.end_date,
    };
    setDateRange(nextRange);
    setCustomerProductFilters((prev) => ({
      ...prev,
      start_date: selectedFy.start_date,
      end_date: selectedFy.end_date,
    }));
    if (user && hasPermission(user, 'sales_reports', 'view')) {
      fetchReports(nextRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFy?.id]);

  const fetchReports = async (rangeOverride = null) => {
    try {
      setLoading(true);
      // Use the explicit override when provided (e.g. when FY changes), else
      // fall back to current `dateRange` state.
      const effectiveRange = rangeOverride || dateRange;
      // Use the selected Fiscal Year's start year for the per-month report.
      // Falls back to the effective range's start year, then today's year.
      const year =
        (effectiveRange?.start_date && Number(effectiveRange.start_date.slice(0, 4))) ||
        new Date().getFullYear();
      
      // Apply own-scope filter for users restricted to their own records
      const isOwnScope = (user?.view_scope || 'all') === 'own' &&
                         user?.role !== 'admin' && user?.role !== 'super_admin';
      const salesRepFilter = isOwnScope ? { sales_rep_id: user.id } : {};
      
      const [monthly, customersRpt, productsReport, salesreps, analysis, invoicesData, productsList, custList, usersList] = await Promise.all([
        api.get(`/sales/reports/monthly?year=${year}`, { params: salesRepFilter }),
        api.get('/sales/reports/customers', { params: { ...effectiveRange, ...salesRepFilter } }),
        api.get('/sales/reports/products', { params: { ...effectiveRange, ...salesRepFilter } }),
        api.get('/sales/reports/salesreps', { params: { ...effectiveRange, ...salesRepFilter } }),
        api.get('/sales/reports/analysis', { params: { ...effectiveRange, ...salesRepFilter } }),
        api.get('/sales/invoices', { params: { ...effectiveRange, ...salesRepFilter } }),
        api.get('/products'),
        api.get('/customers'),
        api.get('/users').catch(() => ({ data: [] })),
      ]);

      setMonthlyData(monthly.data || []);
      setCustomerData(customersRpt.data || []);
      setProductData(productsReport.data || []);
      setSalesRepData(salesreps.data || []);
      setAnalysisData(analysis.data || { by_category: [], by_brand: [], by_division: [], by_model: [], by_subcategory: [] });
      
      // NEW: Store invoices and products for filtering
      setInvoices(invoicesData.data || []);
      setProducts(productsList.data || []);
      setCustomers(custList.data || []);
      setSalesReps((usersList.data || []).filter((u) => u.role === 'sales' || u.role === 'admin'));
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Apply customer-product filters
  const applyCustomerProductFilter = () => {
    const allTransactions = [];
    
    invoices.forEach(invoice => {
      const invoiceDate = invoice.invoice_date;
      
      // Filter by date range
      if (customerProductFilters.start_date && invoiceDate < customerProductFilters.start_date) return;
      if (customerProductFilters.end_date && invoiceDate > customerProductFilters.end_date) return;
      
      // Filter by customer
      if (customerProductFilters.customer_id && invoice.customer_id !== customerProductFilters.customer_id) return;
      
      // Process items
      (invoice.items || []).forEach(item => {
        let matches = true;
        
        // Filter by product name OR part number (if either is specified)
        if (customerProductFilters.product_name || customerProductFilters.part_number) {
          const productNameMatch = customerProductFilters.product_name && 
            item.product_name?.toLowerCase().includes(customerProductFilters.product_name.toLowerCase());
          
          const partNumberMatch = customerProductFilters.part_number && 
            item.part_number?.toLowerCase().includes(customerProductFilters.part_number.toLowerCase());
          
          // Match if EITHER product name OR part number matches (OR logic)
          matches = productNameMatch || partNumberMatch;
        }
        
        if (matches) {
          allTransactions.push({
            invoice_number: invoice.invoice_number,
            invoice_date: invoice.invoice_date,
            customer_id: invoice.customer_id,
            customer_name: invoice.customer_name,
            product_name: item.product_name,
            part_number: item.part_number,
            category: item.category,
            brand: item.brand,
            division: item.division,
            quantity: item.quantity,
            unit_price: item.unit_price,
            total: item.total
          });
        }
      });
    });
    
    setFilteredTransactions(allTransactions);
  };

  // NEW: Product search handlers
  const handleProductSearch = (value) => {
    setProductSearchTerm(value);
    setShowProductDropdown(true);
  };

  const handleProductSelect = (product) => {
    setCustomerProductFilters(prev => ({ 
      ...prev, 
      product_name: product.name,
      part_number: product.part_number || ''  // Auto-fill part number
    }));
    setProductSearchTerm(product.name);
    setPartNumberSearchTerm(product.part_number || '');  // Auto-fill part number field
    setShowProductDropdown(false);
  };

  const getFilteredProducts = () => {
    if (!productSearchTerm) return products;
    return products.filter(product =>
      product.name?.toLowerCase().includes(productSearchTerm.toLowerCase())
    );
  };

  // NEW: Part number search handlers
  const handlePartNumberSearch = (value) => {
    setPartNumberSearchTerm(value);
    setShowPartNumberDropdown(true);
  };

  const handlePartNumberSelect = (product) => {
    setCustomerProductFilters(prev => ({ 
      ...prev, 
      part_number: product.part_number,
      product_name: product.name  // Auto-fill product name
    }));
    setPartNumberSearchTerm(product.part_number);
    setProductSearchTerm(product.name);  // Auto-fill product name field
    setShowPartNumberDropdown(false);
  };

  const getFilteredProductsByPartNumber = () => {
    if (!partNumberSearchTerm) return products.filter(p => p.part_number);
    return products.filter(product =>
      product.part_number?.toLowerCase().includes(partNumberSearchTerm.toLowerCase())
    );
  };

  const applyDateFilter = () => {
    fetchReports();
  };

  // Filter functions for each report
  const filteredCustomerData = customerData.filter(customer =>
    customer.customer_name?.toLowerCase().includes(searchCustomer.toLowerCase())
  );

  const filteredProductData = productData.filter(product =>
    product.product_name?.toLowerCase().includes(searchProduct.toLowerCase()) ||
    product.part_number?.toLowerCase().includes(searchProduct.toLowerCase())
  );

  const filteredSalesRepData = salesRepData.filter(rep =>
    rep.sales_rep_name?.toLowerCase().includes(searchSalesRep.toLowerCase())
  );

  const filteredCategoryData = (analysisData.by_category || []).filter(cat =>
    cat.category?.toLowerCase().includes(searchCategory.toLowerCase()) ||
    cat.name?.toLowerCase().includes(searchCategory.toLowerCase())
  );

  const filteredBrandData = (analysisData.by_brand || []).filter(brand =>
    brand.brand?.toLowerCase().includes(searchBrand.toLowerCase()) ||
    brand.name?.toLowerCase().includes(searchBrand.toLowerCase())
  );

  const filteredDivisionData = (analysisData.by_division || []).filter(div =>
    div.division?.toLowerCase().includes(searchDivision.toLowerCase()) ||
    div.name?.toLowerCase().includes(searchDivision.toLowerCase())
  );

  const filteredModelData = (analysisData.by_model || []).filter(model =>
    model.name?.toLowerCase().includes(searchModel.toLowerCase())
  );

  const filteredSubCategoryData = (analysisData.by_subcategory || []).filter(subcat =>
    subcat.name?.toLowerCase().includes(searchSubCategory.toLowerCase())
  );

  const exportToExcel = (data, filename, sheetTitle = 'Report') => {
    if (!data || data.length === 0) {
      alert('No data to export.');
      return;
    }
    // Proper XLSX export — keeps column headers, numeric typing, and an
    // auto-appended TOTAL row for numeric columns. Easier for finance to
    // open & filter than the previous CSV approach.
    const headers = Object.keys(data[0]);
    const rows = data.map((r) => headers.map((h) => r[h] ?? ''));
    // Compute totals for purely-numeric columns.
    const totals = headers.map((h, i) => {
      if (i === 0) return 'TOTAL';
      const vals = data.map((r) => r[h]);
      const numericVals = vals.filter((v) => typeof v === 'number' && !Number.isNaN(v));
      if (numericVals.length === vals.length && vals.length > 0) {
        return numericVals.reduce((a, b) => a + b, 0);
      }
      return '';
    });
    const aoa = [headers, ...rows, totals];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Auto-size columns based on widest content.
    ws['!cols'] = headers.map((h, i) => {
      const maxLen = Math.max(
        h.length,
        ...aoa.slice(1).map((row) => String(row[i] ?? '').length)
      );
      return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetTitle.slice(0, 30));
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  // Customer Ledger fetcher — pulls sales + receipts for the selected customer
  const fetchCustomerLedger = async () => {
    if (!ledgerFilters.customer_id) return;
    try {
      setLedgerLoading(true);
      // Strip empty values so backend treats unset filters as "no filter"
      const params = Object.fromEntries(
        Object.entries(ledgerFilters).filter(([_, v]) => v !== '' && v != null)
      );
      const res = await api.get('/sales/reports/customer-ledger', { params });
      setLedger(res.data);
    } catch (e) {
      console.error('Failed to fetch ledger:', e);
      setLedger(null);
    } finally {
      setLedgerLoading(false);
    }
  };

  const exportLedgerXlsx = () => {
    if (!ledger) return;
    // Mirror the printed Statement of Accounts: header block with company /
    // customer, "STATEMENT OF ACCOUNTS" centered, period, then the table.
    // When pending_only is on, switch to a 4-column outstanding-bills layout
    // (Date / Invoice # / Description / Amount Due).
    const pendingMode = !!ledger.pending_only;
    const company = companySettings || {};
    const customer = customers.find((c) => c.id === ledger.customer_id) || {};

    const fmt = (n) => (typeof n === 'number' ? Number(n.toFixed(3)) : n);
    const period =
      ledger.start_date && ledger.end_date
        ? `Period: ${formatDateDMY(ledger.start_date)} → ${formatDateDMY(ledger.end_date)}`
        : 'All-time';

    const aoa = [];
    // Top header — company left, customer right (col A vs col E)
    aoa.push([company.name || '', '', '', '', customer.name || '']);
    aoa.push([company.address || '', '', '', '', customer.address || '']);
    aoa.push([
      [company.phone && `Tel: ${company.phone}`, company.email].filter(Boolean).join(' · '),
      '', '', '',
      [customer.phone && `Tel: ${customer.phone}`, customer.email].filter(Boolean).join(' · '),
    ]);
    aoa.push([
      company.tax_id ? `VAT / TRN: ${company.tax_id}` : '',
      '', '', '',
      customer.vat_reg_no ? `VAT: ${customer.vat_reg_no}` : '',
    ]);
    aoa.push([]);
    const heading = pendingMode ? 'OUTSTANDING BILLS STATEMENT' : 'STATEMENT OF ACCOUNTS';
    aoa.push(['', '', heading]);
    aoa.push(['', '', period]);
    aoa.push([]);

    if (pendingMode) {
      aoa.push(['Date', 'Invoice #', 'Description', 'Amount Due']);
      for (const e of ledger.entries || []) {
        aoa.push([
          e.date,
          e.reference,
          `${e.description}${e.payment_status ? ' [' + e.payment_status + ']' : ''}`,
          e.debit ? fmt(e.debit) : '',
        ]);
      }
      aoa.push(['', '', 'Total Outstanding', fmt(ledger.total_debit)]);
    } else {
      aoa.push(['Date', 'Reference', 'Description', 'Debit', 'Credit', 'Balance']);
      aoa.push(['', '', 'Opening Balance', '', '', fmt(ledger.opening_balance)]);
      for (const e of ledger.entries || []) {
        aoa.push([
          e.date,
          e.reference,
          e.description,
          e.debit ? fmt(e.debit) : '',
          e.credit ? fmt(e.credit) : '',
          fmt(e.balance),
        ]);
      }
      aoa.push(['', '', 'Total Debit', fmt(ledger.total_debit), '', '']);
      aoa.push(['', '', 'Total Credit', '', fmt(ledger.total_credit), '']);
      aoa.push(['', '', 'Closing Balance', '', '', fmt(ledger.closing_balance)]);
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Column widths for readability
    ws['!cols'] = pendingMode
      ? [
          { wch: 14 }, { wch: 22 }, { wch: 48 }, { wch: 16 }, { wch: 14 },
        ]
      : [
          { wch: 14 }, { wch: 18 }, { wch: 32 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
        ];
    // Merge centered heading + period across C:E
    ws['!merges'] = [
      { s: { r: 5, c: 2 }, e: { r: 5, c: 4 } },
      { s: { r: 6, c: 2 }, e: { r: 6, c: 4 } },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, pendingMode ? 'Outstanding' : 'Statement');
    const safeName = (ledger.customer_name || 'customer').replace(/[^a-z0-9]+/gi, '_');
    const prefix = pendingMode ? 'outstanding' : 'statement';
    XLSX.writeFile(wb, `${prefix}_${safeName}.xlsx`);
  };

  /**
   * Focused report PDF — pulls ONLY the active tab's table (not a
   * `window.print()` of the entire page). Opens a clean iframe with a
   * company-letterhead header, the report's title + filters, the data
   * table, and a TOTAL row. Fires the browser's print dialog after all
   * images have loaded so the user can save as PDF or print on paper.
   */
  const exportToPDF = () => {
    const dateRangeLabel = (dateRange.start_date || dateRange.end_date)
      ? `${dateRange.start_date || '—'} → ${dateRange.end_date || '—'}`
      : 'All dates';

    const tabsConfig = {
      monthly: {
        title: 'Monthly Sales Report',
        rows: monthlyData,
        columns: [
          { key: 'month', label: 'Month' },
          { key: 'sales_value', label: 'Sales Value', numeric: true },
          { key: 'vat_amount', label: 'VAT', numeric: true },
          { key: 'net_sales', label: 'Net Sales', numeric: true },
          { key: 'invoice_count', label: 'Invoices', numeric: true },
        ],
      },
      customers: {
        title: 'Customer Sales Report',
        rows: filteredCustomerData,
        columns: [
          { key: 'customer_name', label: 'Customer' },
          { key: 'total_sales', label: 'Total Sales', numeric: true },
          { key: 'vat_amount', label: 'VAT', numeric: true },
          { key: 'net_sales', label: 'Net Sales', numeric: true },
          { key: 'invoice_count', label: 'Invoices', numeric: true },
        ],
      },
      products: {
        title: 'Product Sales Report',
        rows: filteredProductData,
        columns: [
          { key: 'product_name', label: 'Product' },
          { key: 'quantity_sold', label: 'Qty Sold', numeric: true },
          { key: 'total_revenue', label: 'Revenue', numeric: true },
        ],
      },
      salesreps: {
        title: 'Sales Rep Performance',
        rows: filteredSalesRepData,
        columns: [
          { key: 'sales_rep_name', label: 'Sales Rep' },
          { key: 'total_sales', label: 'Total Sales', numeric: true },
          { key: 'invoice_count', label: 'Invoices', numeric: true },
        ],
      },
    };
    const cfg = tabsConfig[activeTab];
    if (!cfg || !cfg.rows || cfg.rows.length === 0) {
      alert('No data to export for the current report tab.');
      return;
    }

    const fmt = (v, numeric) => {
      if (v == null || v === '') return '';
      if (numeric && typeof v === 'number') return v.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
      return String(v);
    };

    // Totals row (sum of numeric columns)
    const totalsRow = cfg.columns.map((col, idx) => {
      if (idx === 0) return 'TOTAL';
      if (!col.numeric) return '';
      const sum = cfg.rows.reduce((acc, r) => acc + (Number(r[col.key]) || 0), 0);
      return fmt(sum, true);
    });

    const headerHtml = `
      <div class="lh">
        ${companySettings?.logo_url ? `<img class="lh-logo" src="${companySettings.logo_url}" alt="" />` : ''}
        <div class="lh-meta">
          <div class="lh-name">${(companySettings?.name || '').replace(/</g, '&lt;')}</div>
          ${companySettings?.tax_id ? `<div class="lh-line">VAT: ${companySettings.tax_id}</div>` : ''}
          ${companySettings?.cr_number ? `<div class="lh-line">CR: ${companySettings.cr_number}</div>` : ''}
        </div>
        ${companySettings?.document_header_url ? `<img class="lh-banner" src="${companySettings.document_header_url}" alt="" />` : ''}
      </div>
    `;
    const tableHtml = `
      <table>
        <thead>
          <tr>${cfg.columns.map((c) => `<th class="${c.numeric ? 'r' : ''}">${c.label}</th>`).join('')}</tr>
        </thead>
        <tbody>
          ${cfg.rows.map((r) => `<tr>${cfg.columns.map((c) => `<td class="${c.numeric ? 'r' : ''}">${fmt(r[c.key], c.numeric)}</td>`).join('')}</tr>`).join('')}
          <tr class="totals">${totalsRow.map((v, i) => `<td class="${cfg.columns[i]?.numeric ? 'r' : ''}">${v}</td>`).join('')}</tr>
        </tbody>
      </table>
    `;
    const html = `
      <!doctype html><html><head><title>${cfg.title}</title>
      <style>
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Segoe UI', Arial, sans-serif; color: #111; }
        .page { padding: 14mm 12mm; }
        .lh { display: flex; align-items: center; gap: 12px; border-bottom: 2px solid #1d4ed8; padding-bottom: 8px; margin-bottom: 10px; }
        .lh-logo { max-height: 56px; max-width: 130px; object-fit: contain; }
        .lh-banner { max-height: 56px; max-width: 240px; margin-left: auto; object-fit: contain; }
        .lh-name { font-size: 16pt; font-weight: 800; color: #0f172a; }
        .lh-line { font-size: 9pt; color: #64748b; }
        .title-row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px; }
        .doc-title { font-size: 14pt; font-weight: 700; color: #0f172a; }
        .filters { font-size: 9pt; color: #475569; }
        table { width: 100%; border-collapse: collapse; font-size: 10pt; margin-top: 4px; }
        th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; }
        th { background: #f1f5f9; font-size: 9pt; color: #475569; text-transform: uppercase; letter-spacing: 0.4px; }
        td.r, th.r { text-align: right; font-variant-numeric: tabular-nums; }
        tr.totals td { background: #eff6ff; font-weight: 700; }
        .meta { font-size: 8pt; color: #94a3b8; margin-top: 10px; text-align: right; }
        @media print { @page { size: A4 portrait; margin: 8mm; } }
      </style></head>
      <body>
        <div class="page">
          ${headerHtml}
          <div class="title-row">
            <div class="doc-title">${cfg.title}</div>
            <div class="filters">Period: ${dateRangeLabel}</div>
          </div>
          ${tableHtml}
          <div class="meta">Generated ${new Date().toLocaleString()} · ${cfg.rows.length} row${cfg.rows.length === 1 ? '' : 's'}</div>
        </div>
      </body></html>
    `;

    // Render into a hidden iframe so the host page CSS doesn't pollute the
    // PDF, and fire print() only after every image (logo, header banner)
    // has loaded — same pattern used by InvoicePrintView / PaymentReceiptPrint.
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.open();
    doc.write(html);
    doc.close();
    const fireWhenReady = () => {
      const imgs = Array.from(doc.images || []);
      const waiters = imgs.map((img) =>
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((res) => {
              const done = () => res();
              img.addEventListener('load', done, { once: true });
              img.addEventListener('error', done, { once: true });
              setTimeout(done, 3000);
            })
      );
      Promise.all(waiters).then(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch { /* noop */ }
        // Detach after the print dialog closes — give the browser a
        // moment so the dialog isn't killed early.
        setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1500);
      });
    };
    setTimeout(fireWhenReady, 200);
  };

  // Chart configurations
  const monthlyChartData = {
    labels: monthlyData.map(d => d.month),
    datasets: [
      {
        label: 'Sales Value',
        data: monthlyData.map(d => d.sales_value),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        tension: 0.3
      },
      {
        label: 'VAT Amount',
        data: monthlyData.map(d => d.vat_amount),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        tension: 0.3
      }
    ]
  };

  const customerChartData = {
    labels: customerData.slice(0, 10).map(d => d.customer_name),
    datasets: [{
      label: 'Total Sales',
      data: customerData.slice(0, 10).map(d => d.total_sales),
      backgroundColor: 'rgba(59, 130, 246, 0.6)'
    }]
  };

  const salesRepChartData = {
    labels: salesRepData.map(d => d.sales_rep_name),
    datasets: [{
      label: 'Total Sales (excl. VAT)',
      data: salesRepData.map(d => d.total_sales_excl_vat ?? d.total_sales),
      backgroundColor: 'rgba(16, 185, 129, 0.6)'
    }]
  };

  const categoryPieData = {
    labels: analysisData.by_category.map(d => d.name),
    datasets: [{
      data: analysisData.by_category.map(d => d.total_sales),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(139, 92, 246, 0.8)',
        'rgba(236, 72, 153, 0.8)'
      ]
    }]
  };

  const brandPieData = {
    labels: analysisData.by_brand.map(d => d.name),
    datasets: [{
      data: analysisData.by_brand.map(d => d.total_sales),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(139, 92, 246, 0.8)'
      ]
    }]
  };

  const divisionPieData = {
    labels: analysisData.by_division.map(d => d.name),
    datasets: [{
      data: analysisData.by_division.map(d => d.total_sales),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',   // Blue
        'rgba(16, 185, 129, 0.8)',   // Green
        'rgba(251, 191, 36, 0.8)',   // Yellow
        'rgba(239, 68, 68, 0.8)',    // Red
        'rgba(139, 92, 246, 0.8)',   // Purple
        'rgba(236, 72, 153, 0.8)'    // Pink
      ]
    }]
  };

  const modelPieData = {
    labels: analysisData.by_model.map(d => d.name),
    datasets: [{
      data: analysisData.by_model.map(d => d.total_sales),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(139, 92, 246, 0.8)',
        'rgba(236, 72, 153, 0.8)',
        'rgba(6, 182, 212, 0.8)',
        'rgba(245, 158, 11, 0.8)'
      ]
    }]
  };

  const subCategoryPieData = {
    labels: analysisData.by_subcategory.map(d => d.name),
    datasets: [{
      data: analysisData.by_subcategory.map(d => d.total_sales),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(139, 92, 246, 0.8)',
        'rgba(236, 72, 153, 0.8)',
        'rgba(6, 182, 212, 0.8)',
        'rgba(245, 158, 11, 0.8)'
      ]
    }]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/dashboard'}
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
          >
            ← Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
          📊 {((user?.view_scope || 'all') === 'own' && user?.role !== 'admin' && user?.role !== 'super_admin') ? 'My Sales Reports' : 'Sales Reports'}
        </h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => exportToPDF()} variant="outline" className="text-red-600 border-red-600">
            📄 Export PDF
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-end gap-4">
            <DateRangeFilter
              startDate={dateRange.start_date}
              endDate={dateRange.end_date}
              onStartChange={(value) => setDateRange(prev => ({ ...prev, start_date: value }))}
              onEndChange={(value) => setDateRange(prev => ({ ...prev, end_date: value }))}
              className="flex-1 bg-transparent p-0 border-0"
            />
            <Button onClick={applyDateFilter} className="bg-blue-600 hover:bg-blue-700 mb-3">
              Apply Filter
            </Button>
            <Button
              onClick={() => {
                setDateRange({ start_date: '', end_date: '' });
                setTimeout(fetchReports, 100);
              }}
              variant="outline"
              className="mb-3"
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {[
          { id: 'monthly', label: '📅 Monthly Sales', icon: '📅' },
          { id: 'customers', label: '👥 Customers', icon: '👥' },
          { id: 'products', label: '📦 Products', icon: '📦' },
          { id: 'customer-products', label: '🛒 Customer Products', icon: '🛒' },  // NEW TAB
          { id: 'salesreps', label: '👤 Sales Reps', icon: '👤' },
          { id: 'customer-ledger', label: '📒 Customer Ledger', icon: '📒' },
          { id: 'debtors-aging', label: '📊 Debtors Aging', icon: '📊' },
          { id: 'analysis', label: '🏷️ Analysis', icon: '🏷️' }
        ].map(tab => (
          <Button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Monthly Sales Report */}
      {activeTab === 'monthly' && (
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Monthly Sales Report ({new Date().getFullYear()})</CardTitle>
              <Button onClick={() => exportToExcel(monthlyData, 'monthly_sales_report', 'Monthly Sales')} size="sm" variant="outline">
                📊 Export Excel
              </Button>
              <Button onClick={exportToPDF} size="sm" variant="outline" className="ml-2 text-red-700 border-red-300" data-testid="monthly-export-pdf">
                📄 Export PDF
              </Button>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <Line data={monthlyChartData} options={{ responsive: true, maintainAspectRatio: true }} />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">VAT 10%</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net Sales</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {monthlyData.map((row) => (
                      <tr key={row.month}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.month}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatAmount(row.sales_value)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatAmount(row.vat_amount)}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatAmount(row.net_sales)}</td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50 font-bold">
                      <td className="px-4 py-3 text-sm">TOTAL</td>
                      <td className="px-4 py-3 text-sm">{formatAmount(monthlyData.reduce((sum, r) => sum + r.sales_value, 0))}</td>
                      <td className="px-4 py-3 text-sm">{formatAmount(monthlyData.reduce((sum, r) => sum + r.vat_amount, 0))}</td>
                      <td className="px-4 py-3 text-sm">{formatAmount(monthlyData.reduce((sum, r) => sum + r.net_sales, 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Customer Sales Report */}
      {activeTab === 'customers' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Customer Sales Report</CardTitle>
            <Button onClick={() => exportToExcel(filteredCustomerData, 'customer_sales_report', 'Customers')} size="sm" variant="outline">
              📊 Export Excel
            </Button>
            <Button onClick={exportToPDF} size="sm" variant="outline" className="ml-2 text-red-700 border-red-300" data-testid="customers-export-pdf">
              📄 Export PDF
            </Button>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="🔍 Search customers..."
                value={searchCustomer}
                onChange={(e) => setSearchCustomer(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="customer-report-search"
              />
              {searchCustomer && (
                <p className="text-sm text-gray-600 mt-2">
                  Showing {filteredCustomerData.length} of {customerData.length} customers
                </p>
              )}
            </div>

            <div className="mb-6">
              <Bar data={customerChartData} options={{ responsive: true, maintainAspectRatio: true }} />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Number of Invoices</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCustomerData.map((row) => (
                    <tr key={row.customer_id || row.customer_name}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.customer_name}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatAmount(row.total_sales)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.invoice_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Product Sales Report */}
      {activeTab === 'products' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Product/Item Sales Report</CardTitle>
            <Button onClick={() => exportToExcel(filteredProductData, 'product_sales_report', 'Products')} size="sm" variant="outline">
              📊 Export Excel
            </Button>
            <Button onClick={exportToPDF} size="sm" variant="outline" className="ml-2 text-red-700 border-red-300" data-testid="products-export-pdf">
              📄 Export PDF
            </Button>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="🔍 Search products by name or part number..."
                value={searchProduct}
                onChange={(e) => setSearchProduct(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="product-report-search"
              />
              {searchProduct && (
                <p className="text-sm text-gray-600 mt-2">
                  Showing {filteredProductData.length} of {productData.length} products
                </p>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Number</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Division</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty Sold</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProductData.map((row) => (
                    <tr key={`${row.part_number || ''}-${row.product_name}`}>
                      <td className="px-4 py-3 text-sm font-medium text-blue-600">{row.part_number || '-'}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.product_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.category || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.brand || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.division || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.total_qty}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatAmount(row.total_sales)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* NEW: Customer Product Purchase Report */}
      {activeTab === 'customer-products' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Customer Product Purchase Report</CardTitle>
            <Button 
              onClick={() => {
                if (filteredTransactions.length === 0) {
                  alert('Please apply filters first to see results');
                  return;
                }
                const exportData = filteredTransactions.map(t => ({
                  'Invoice Date': t.invoice_date,
                  'Invoice Number': t.invoice_number,
                  'Customer': t.customer_name,
                  'Part Number': t.part_number || '-',
                  'Product Name': t.product_name,
                  'Category': t.category || '-',
                  'Brand': t.brand || '-',
                  'Quantity': t.quantity,
                  'Unit Price': t.unit_price.toFixed(3),
                  'Total': t.total.toFixed(3)
                }));
                exportToExcel(exportData, 'customer_product_purchase_report');
              }} 
              size="sm" 
              variant="outline"
            >
              📊 Export Excel
            </Button>
          </CardHeader>
          <CardContent>
            {/* Filter Panel */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg mb-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">🔍 Filter Criteria</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Date Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                  <DateInputDMY value={customerProductFilters.start_date}
 onChange={(e) => setCustomerProductFilters(prev => ({ ...prev, start_date: e.target.value }))}
 className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
 />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                  <DateInputDMY value={customerProductFilters.end_date}
 onChange={(e) => setCustomerProductFilters(prev => ({ ...prev, end_date: e.target.value }))}
 className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
 />
                </div>
                
                {/* Customer Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                  <SearchableSelect
                    value={customerProductFilters.customer_id}
                    onChange={(val) => setCustomerProductFilters(prev => ({ ...prev, customer_id: val }))}
                    options={customerData.map((customer) => ({ value: customer.customer_id, label: customer.customer_name }))}
                    placeholder="All Customers"
                  />
                </div>
                
                {/* Part Number Filter - NEW SEARCHABLE DROPDOWN */}
                <div className="relative part-number-search-dropdown">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Part Number</label>
                  <input
                    type="text"
                    placeholder="Search part number..."
                    value={partNumberSearchTerm}
                    onChange={(e) => handlePartNumberSearch(e.target.value)}
                    onFocus={() => setShowPartNumberDropdown(true)}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  {showPartNumberDropdown && getFilteredProductsByPartNumber().length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {getFilteredProductsByPartNumber().map((product) => (
                        <div
                          key={product.id}
                          onClick={() => handlePartNumberSelect(product)}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100"
                        >
                          <div className="font-medium text-blue-600">{product.part_number}</div>
                          <div className="text-xs text-gray-500">{product.name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Product Name Filter - NEW SEARCHABLE DROPDOWN */}
                <div className="relative product-search-dropdown">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                  <input
                    type="text"
                    placeholder="Search product name..."
                    value={productSearchTerm}
                    onChange={(e) => handleProductSearch(e.target.value)}
                    onFocus={() => setShowProductDropdown(true)}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  {showProductDropdown && getFilteredProducts().length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {getFilteredProducts().map((product) => (
                        <div
                          key={product.id}
                          onClick={() => handleProductSelect(product)}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100"
                        >
                          <div className="font-medium text-gray-900">{product.name}</div>
                          <div className="text-xs text-gray-500">
                            {product.part_number && `Part #: ${product.part_number}`}
                            {product.category && ` • ${product.category}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Filter Buttons */}
              <div className="flex gap-3 mt-4">
                <Button 
                  onClick={applyCustomerProductFilter} 
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  🔍 Apply Filter
                </Button>
                <Button 
                  onClick={() => {
                    setCustomerProductFilters({
                      start_date: '',
                      end_date: '',
                      customer_id: '',
                      product_name: '',
                      part_number: ''
                    });
                    setProductSearchTerm('');
                    setPartNumberSearchTerm('');
                    setFilteredTransactions([]);
                  }}
                  variant="outline"
                  className="border-gray-300"
                >
                  🔄 Clear Filters
                </Button>
              </div>
            </div>

            {/* Summary Stats */}
            {filteredTransactions.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-600">Total Transactions</p>
                  <p className="text-2xl font-bold text-blue-600">{filteredTransactions.length}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-600">Total Quantity</p>
                  <p className="text-2xl font-bold text-green-600">
                    {filteredTransactions.reduce((sum, t) => sum + t.quantity, 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-gray-600">Total Value</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatAmount(filteredTransactions.reduce((sum, t) => sum + t.total, 0))}
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <p className="text-sm text-gray-600">Unique Products</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {new Set(filteredTransactions.map(t => t.product_name)).size}
                  </p>
                </div>
              </div>
            )}

            {/* Results Table */}
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500 text-lg mb-2">📋 No results to display</p>
                <p className="text-gray-400 text-sm">Please select filters above and click "Apply Filter" to view data</p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Number</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTransactions.map((transaction, idx) => (
                      <tr key={`${transaction.invoice_number}-${transaction.part_number || ''}-${idx}`} className="hover:bg-blue-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDateDMY(transaction.invoice_date)}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{transaction.customer_name}</td>
                        <td className="px-4 py-3 text-sm font-medium text-blue-600">{transaction.part_number || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{transaction.product_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{transaction.category || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{transaction.brand || '-'}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{transaction.quantity}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatAmount(transaction.unit_price)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-green-600">{formatAmount(transaction.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sales Rep Performance Report */}
      {activeTab === 'salesreps' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Salesperson Performance Report</CardTitle>
            <Button onClick={() => exportToExcel(filteredSalesRepData, 'salesrep_performance_report', 'Sales Reps')} size="sm" variant="outline">
              📊 Export Excel
            </Button>
            <Button onClick={exportToPDF} size="sm" variant="outline" className="ml-2 text-red-700 border-red-300" data-testid="salesreps-export-pdf">
              📄 Export PDF
            </Button>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="🔍 Search sales reps..."
                value={searchSalesRep}
                onChange={(e) => setSearchSalesRep(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="salesrep-report-search"
              />
              {searchSalesRep && (
                <p className="text-sm text-gray-600 mt-2">
                  Showing {filteredSalesRepData.length} of {salesRepData.length} sales reps
                </p>
              )}
            </div>

            <div className="mb-6">
              <Bar data={salesRepChartData} options={{ responsive: true, maintainAspectRatio: true }} />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales Rep</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Sales (excl. VAT)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Achievement</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoices</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSalesRepData.map((row) => (
                    <tr key={row.sales_rep_id || row.sales_rep_name}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.sales_rep_name}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatAmount((row.total_sales_excl_vat ?? row.total_sales))}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {row.monthly_target ? `${formatAmount(row.monthly_target)}` : 'Not Set'}
                      </td>
                      <td className="px-4 py-3">
                        {row.achievement_percentage ? (
                          <div>
                            <div className={`text-sm font-semibold ${
                              row.achievement_percentage >= 100 ? 'text-green-600' :
                              row.achievement_percentage >= 75 ? 'text-blue-600' :
                              row.achievement_percentage >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {row.achievement_percentage.toFixed(1)}%
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className={`h-2 rounded-full ${
                                  row.achievement_percentage >= 100 ? 'bg-green-500' :
                                  row.achievement_percentage >= 75 ? 'bg-blue-500' :
                                  row.achievement_percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(row.achievement_percentage, 100)}%` }}
                              />
                            </div>
                          </div>
                        ) : <span className="text-gray-400 text-sm">N/A</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.invoice_count}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600">{formatAmount(row.commission)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customer Ledger Report */}
      {activeTab === 'customer-ledger' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>📒 Customer Ledger — Sales & Receipts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-sm mb-1">Customer *</label>
                  <SearchableSelect
                    value={ledgerFilters.customer_id}
                    onChange={(v) => setLedgerFilters({ ...ledgerFilters, customer_id: v })}
                    options={customers.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder="Select customer"
                    data-testid="ledger-customer-select"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">From</label>
                  <DateInputDMY
                    value={ledgerFilters.start_date}
                    onChange={(e) => setLedgerFilters({ ...ledgerFilters, start_date: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">To</label>
                  <DateInputDMY
                    value={ledgerFilters.end_date}
                    onChange={(e) => setLedgerFilters({ ...ledgerFilters, end_date: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Status</label>
                  <select
                    value={ledgerFilters.payment_status}
                    onChange={(e) => setLedgerFilters({ ...ledgerFilters, payment_status: e.target.value })}
                    disabled={!!ledgerFilters.pending_only}
                    className="w-full border rounded px-3 py-2 bg-white disabled:bg-gray-100 disabled:text-gray-400"
                    title={ledgerFilters.pending_only ? 'Disabled while "Pending Bills Only" is on' : ''}
                  >
                    <option value="">All statuses</option>
                    <option value="Paid">Paid</option>
                    <option value="Pending">Pending</option>
                    <option value="Partial">Partial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">Sales Rep</label>
                  <SearchableSelect
                    value={ledgerFilters.sales_rep_id}
                    onChange={(v) => setLedgerFilters({ ...ledgerFilters, sales_rep_id: v })}
                    options={[{ value: '', label: 'All sales reps' }, ...salesReps.map((u) => ({ value: u.id, label: u.name }))]}
                    placeholder="All sales reps"
                  />
                </div>
                <div className="md:col-span-5 flex items-center gap-2 -mt-1">
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none px-2 py-1 rounded border bg-amber-50 border-amber-200 hover:bg-amber-100">
                    <input
                      type="checkbox"
                      checked={!!ledgerFilters.pending_only}
                      onChange={(e) => setLedgerFilters({ ...ledgerFilters, pending_only: e.target.checked })}
                      data-testid="ledger-pending-only-checkbox"
                    />
                    <span className="font-medium text-amber-900">📌 Pending Bills Only</span>
                    <span className="text-xs text-amber-700">(hides receipts & paid invoices)</span>
                  </label>
                </div>
                <div className="md:col-span-4 flex items-end gap-2">
                  <Button
                    onClick={fetchCustomerLedger}
                    disabled={!ledgerFilters.customer_id || ledgerLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="ledger-generate-btn"
                  >
                    {ledgerLoading ? 'Loading…' : 'Generate Ledger'}
                  </Button>
                  {ledger && (
                    <>
                      <Button onClick={exportLedgerXlsx} variant="outline" size="sm" data-testid="ledger-export-excel">
                        📊 Export Excel
                      </Button>
                      <Button
                        onClick={() => setPrintingLedger(ledger)}
                        variant="outline"
                        size="sm"
                        data-testid="ledger-print-pdf"
                      >
                        🖨 Print / PDF
                      </Button>
                      <Button
                        onClick={() => setEmailingLedger(true)}
                        variant="outline"
                        size="sm"
                        data-testid="ledger-email-btn"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        ✉️ Send by Email
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {ledger && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>
                    {ledger.customer_name}
                    {ledger.pending_only && (
                      <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">
                        Pending Bills Only
                      </span>
                    )}
                  </CardTitle>
                  <p className="text-xs text-gray-500">
                    {ledger.start_date ? `From ${formatDateDMY(ledger.start_date)}` : 'All-time'}
                    {ledger.end_date ? ` to ${formatDateDMY(ledger.end_date)}` : ''}
                  </p>
                </div>
                <div className="text-right text-sm">
                  {ledger.pending_only ? (
                    <div>
                      Total Outstanding:
                      <span className="ml-2 font-bold text-red-600">
                        {formatAmount(ledger.total_debit)}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div>Closing Balance:
                        <span className={`ml-2 font-bold ${ledger.closing_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatAmount(ledger.closing_balance)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Debit: {formatAmount(ledger.total_debit)} · Credit: {formatAmount(ledger.total_credit)}
                      </div>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          {ledger.pending_only ? 'Invoice #' : 'Reference'}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        {ledger.pending_only ? (
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount Due</th>
                        ) : (
                          <>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {!ledger.pending_only && (
                        <tr className="bg-blue-50">
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 font-medium">Opening Balance</td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-right font-semibold">{formatAmount(ledger.opening_balance)}</td>
                        </tr>
                      )}
                      {(ledger.entries || []).map((e, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">{formatDateDMY(e.date)}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{e.reference}</td>
                          <td className="px-3 py-2">
                            {e.description}
                            {e.payment_status && (
                              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${ledger.pending_only ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>{e.payment_status}</span>
                            )}
                          </td>
                          {ledger.pending_only ? (
                            <td className="px-3 py-2 text-right font-semibold text-red-600">{e.debit ? formatAmount(e.debit) : ''}</td>
                          ) : (
                            <>
                              <td className="px-3 py-2 text-right text-red-600">{e.debit ? formatAmount(e.debit) : ''}</td>
                              <td className="px-3 py-2 text-right text-green-600">{e.credit ? formatAmount(e.credit) : ''}</td>
                              <td className="px-3 py-2 text-right font-semibold">{formatAmount(e.balance)}</td>
                            </>
                          )}
                        </tr>
                      ))}
                      {(!ledger.entries || ledger.entries.length === 0) && (
                        <tr><td colSpan={ledger.pending_only ? 4 : 6} className="text-center text-gray-400 py-4">
                          {ledger.pending_only ? 'No pending bills in this range.' : 'No transactions in this range.'}
                        </td></tr>
                      )}
                    </tbody>
                    <tfoot className="bg-gray-100 font-semibold">
                      {ledger.pending_only ? (
                        <tr>
                          <td colSpan={3} className="px-3 py-2 text-right">Total Outstanding</td>
                          <td className="px-3 py-2 text-right text-red-700">{formatAmount(ledger.total_debit)}</td>
                        </tr>
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-3 py-2 text-right">Totals</td>
                          <td className="px-3 py-2 text-right text-red-700">{formatAmount(ledger.total_debit)}</td>
                          <td className="px-3 py-2 text-right text-green-700">{formatAmount(ledger.total_credit)}</td>
                          <td className="px-3 py-2 text-right">{formatAmount(ledger.closing_balance)}</td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Ledger Print preview — Statement-of-Accounts style */}
      {printingLedger && (() => {
        const cust = customers.find((c) => c.id === printingLedger.customer_id) || {};
        const periodLabel =
          printingLedger.start_date && printingLedger.end_date
            ? `Period: ${formatDateDMY(printingLedger.start_date)} → ${formatDateDMY(printingLedger.end_date)}`
            : 'All-time';
        const pendingMode = !!printingLedger.pending_only;
        const itemColumns = pendingMode
          ? [
              { key: 'date', label: 'Date' },
              { key: 'reference', label: 'Invoice #' },
              { key: 'description', label: 'Description' },
              { key: 'debit', label: 'Amount Due', align: 'right', numeric: true },
            ]
          : [
              { key: 'date', label: 'Date' },
              { key: 'reference', label: 'Reference' },
              { key: 'description', label: 'Description' },
              { key: 'debit', label: 'Debit', align: 'right', numeric: true },
              { key: 'credit', label: 'Credit', align: 'right', numeric: true },
              { key: 'balance', label: 'Balance', align: 'right', numeric: true },
            ];
        const items = pendingMode
          ? (printingLedger.entries || []).map((e) => ({
              ...e,
              date: e.date ? formatDateDMY(e.date) : '',
            }))
          : [
              {
                date: '',
                reference: '',
                description: 'Opening Balance',
                debit: 0,
                credit: 0,
                balance: printingLedger.opening_balance,
              },
              ...(printingLedger.entries || []).map((e) => ({
                ...e,
                date: e.date ? formatDateDMY(e.date) : '',
              })),
            ];
        const totals = pendingMode
          ? [
              { label: 'Total Outstanding', value: printingLedger.total_debit.toFixed(3), bold: true },
            ]
          : [
              { label: 'Total Debit', value: printingLedger.total_debit.toFixed(3) },
              { label: 'Total Credit', value: printingLedger.total_credit.toFixed(3) },
              { label: 'Closing Balance', value: printingLedger.closing_balance.toFixed(3), bold: true },
            ];
        return (
        <DocumentPrintView
          title={pendingMode ? 'Outstanding Bills Statement' : 'Statement of Accounts'}
          docNumber=""
          docDate={printingLedger.end_date || new Date().toISOString().split('T')[0]}
          headerLayout="statement"
          centerHeading={pendingMode ? 'Outstanding Bills Statement' : 'Statement of Accounts'}
          company={companySettings}
          customer={{
            name: printingLedger.customer_name,
            address: cust.address,
            phone: cust.phone,
            email: cust.email,
            vat_reg_no: cust.vat_reg_no,
            period_label: periodLabel,
          }}
          parties={[]}
          meta={[]}
          itemColumns={itemColumns}
          items={items}
          totals={totals}
          onClose={() => setPrintingLedger(null)}
        />
        );
      })()}

      {/* Email Customer Ledger modal */}
      {emailingLedger && ledger && (
        <EmailLedgerModal
          ledger={ledger}
          filters={ledgerFilters}
          customer={customers.find((c) => c.id === ledger.customer_id)}
          onClose={() => setEmailingLedger(false)}
        />
      )}

      {/* Debtors Aging Report */}
      {activeTab === 'debtors-aging' && <DebtorsAgingTab />}

      {/* Analysis Report */}
      {activeTab === 'analysis' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Sales by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Search Bar */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="🔍 Search categories..."
                  value={searchCategory}
                  onChange={(e) => setSearchCategory(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="category-analysis-search"
                />
                {searchCategory && (
                  <p className="text-sm text-gray-600 mt-2">
                    Showing {filteredCategoryData.length} of {analysisData.by_category.length} categories
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div style={{ height: '600px' }}>
                  <Pie data={categoryPieData} options={{ responsive: true, maintainAspectRatio: true }} />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Category</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Total Sales</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredCategoryData.map((row) => (
                        <tr key={`cat-${row.name}`}>
                          <td className="px-4 py-2 text-sm">{row.name}</td>
                          <td className="px-4 py-2 text-sm font-semibold">{formatAmount(row.total_sales)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Sales by Brand</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Search Bar */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="🔍 Search brands..."
                  value={searchBrand}
                  onChange={(e) => setSearchBrand(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="brand-analysis-search"
                />
                {searchBrand && (
                  <p className="text-sm text-gray-600 mt-2">
                    Showing {filteredBrandData.length} of {analysisData.by_brand.length} brands
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div style={{ height: '600px' }}>
                  <Pie data={brandPieData} options={{ responsive: true, maintainAspectRatio: true }} />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Brand</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Total Sales</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredBrandData.map((row) => (
                        <tr key={`brand-${row.name}`}>
                          <td className="px-4 py-2 text-sm">{row.name}</td>
                          <td className="px-4 py-2 text-sm font-semibold">{formatAmount(row.total_sales)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Sales by Division</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Search Bar */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="🔍 Search divisions..."
                  value={searchDivision}
                  onChange={(e) => setSearchDivision(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="division-analysis-search"
                />
                {searchDivision && (
                  <p className="text-sm text-gray-600 mt-2">
                    Showing {filteredDivisionData.length} of {analysisData.by_division.length} divisions
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div style={{ height: '600px' }}>
                  <Pie data={divisionPieData} options={{ responsive: true, maintainAspectRatio: true }} />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Division</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Total Sales</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredDivisionData.map((row) => (
                        <tr key={`div-${row.name}`}>
                          <td className="px-4 py-2 text-sm">{row.name}</td>
                          <td className="px-4 py-2 text-sm font-semibold">{formatAmount(row.total_sales)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Sales by Model</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Search Bar */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="🔍 Search models..."
                  value={searchModel}
                  onChange={(e) => setSearchModel(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="model-analysis-search"
                />
                {searchModel && (
                  <p className="text-sm text-gray-600 mt-2">
                    Showing {filteredModelData.length} of {analysisData.by_model.length} models
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div style={{ height: '600px' }}>
                  <Pie data={modelPieData} options={{ responsive: true, maintainAspectRatio: true }} />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Model</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Total Sales</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredModelData.map((row) => (
                        <tr key={`model-${row.name}`}>
                          <td className="px-4 py-2 text-sm">{row.name}</td>
                          <td className="px-4 py-2 text-sm font-semibold">{formatAmount(row.total_sales)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Sales by Sub-Category</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Search Bar */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="🔍 Search sub-categories..."
                  value={searchSubCategory}
                  onChange={(e) => setSearchSubCategory(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="subcategory-analysis-search"
                />
                {searchSubCategory && (
                  <p className="text-sm text-gray-600 mt-2">
                    Showing {filteredSubCategoryData.length} of {analysisData.by_subcategory.length} sub-categories
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div style={{ height: '600px' }}>
                  <Pie data={subCategoryPieData} options={{ responsive: true, maintainAspectRatio: true }} />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Sub-Category</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Total Sales</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredSubCategoryData.map((row) => (
                        <tr key={`subcat-${row.name}`}>
                          <td className="px-4 py-2 text-sm">{row.name}</td>
                          <td className="px-4 py-2 text-sm font-semibold">{formatAmount(row.total_sales)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}


// ── Email Ledger Modal ────────────────────────────────────────────────────
function EmailLedgerModal({ ledger, filters, customer, onClose }) {
  const isPending = !!ledger?.pending_only;
  const [to, setTo] = useState(customer?.email || '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(
    `${isPending ? 'Outstanding Bills Statement' : 'Statement of Account'} — ${ledger?.customer_name || 'Customer'}` +
      (ledger?.end_date ? ` (as of ${ledger.end_date})` : '')
  );
  const [message, setMessage] = useState(
    isPending
      ? `Dear ${ledger?.customer_name || 'Customer'},\n\n` +
          `Please find below the list of outstanding bills on your account.\n\n` +
          `Kindly arrange settlement at the earliest. Let us know if you have any questions.`
      : `Dear ${ledger?.customer_name || 'Customer'},\n\n` +
          `Please find your latest statement of account attached.\n\n` +
          `Kindly review and let us know if you have any questions.`
  );
  const [sending, setSending] = useState(false);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!to) {
      alert('Please enter the customer email address.');
      return;
    }
    try {
      setSending(true);
      const ccArr = cc
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      await api.post('/sales/reports/customer-ledger/email', {
        customer_id: ledger.customer_id,
        to_email: to,
        cc: ccArr.length ? ccArr : null,
        start_date: filters?.start_date || null,
        end_date: filters?.end_date || null,
        payment_status: filters?.payment_status || null,
        sales_rep_id: filters?.sales_rep_id || null,
        pending_only: !!filters?.pending_only,
        subject,
        message,
      });
      alert(`✅ Statement sent to ${to}`);
      onClose();
    } catch (err) {
      alert(
        `❌ Failed to send:\n${err.response?.data?.detail || err.message}\n\n` +
          `If this is your first time, set up SMTP in Company Settings → Email Notifications.`
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl my-8" data-testid="ledger-email-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-900">
            ✉️ {isPending ? 'Email Outstanding Bills Statement' : 'Email Statement of Account'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">
            &times;
          </button>
        </div>

        <form onSubmit={handleSend} className="p-6 space-y-4">
          <div className={`text-xs text-gray-700 border rounded p-2 ${isPending ? 'bg-amber-50 border-amber-200' : 'bg-gray-50'}`}>
            For <b>{ledger?.customer_name}</b>
            {ledger?.start_date && ledger?.end_date && (
              <> · Period <b>{ledger.start_date} → {ledger.end_date}</b></>
            )}
            <br />
            {isPending ? (
              <>
                <span className="font-semibold text-amber-800">Pending Bills Only</span> · Total outstanding:{' '}
                <b className="text-red-700">
                  {(ledger?.total_debit || 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                </b>
              </>
            ) : (
              <>
                Closing balance:{' '}
                <b>{(ledger?.closing_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</b>
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">To *</label>
            <input
              type="email"
              required
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="customer@example.com"
              data-testid="ledger-email-to"
            />
            {!customer?.email && (
              <p className="text-xs text-amber-600 mt-1">
                No email on file for this customer — enter the address now (it won't be saved on the customer record).
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">CC <span className="text-gray-400 font-normal">(comma-separated, optional)</span></label>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="accounts@yourcompany.com, manager@…"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Message</label>
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="A short note shown above the statement…"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
              data-testid="ledger-email-send"
            >
              {sending ? 'Sending…' : 'Send Email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
